import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '../ui/LanguageSelector';
import logoSvg from '../../assets/images/logo-roundvoip.svg';

interface AuthLayoutProps {
  children: React.ReactNode;
}

export default function AuthLayout({ children }: AuthLayoutProps) {
  const { pathname } = useLocation();

  return (
    <div className="flex min-h-screen">
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary to-secondary relative overflow-hidden">
        <InteractiveBackground />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <img src={logoSvg} alt="RoundVoIP" className="h-10 brightness-0 invert" />
          <div className="space-y-4">
            <CarouselContent pathname={pathname} />
          </div>
          <div className="text-white/50 text-sm">
            &copy; {new Date().getFullYear()} RoundDesk Solutions
          </div>
        </div>
      </div>
      <div className="flex-1 min-h-screen flex flex-col items-center justify-center p-6 md:p-12 bg-gray-50 relative">
        <LanguageSelector />
        <img src={logoSvg} alt="RoundVoIP" className="h-8 mb-8 lg:hidden" />
        <div className="w-full max-w-md">
          {children}
        </div>
      </div>
    </div>
  );
}

function InteractiveBackground() {
  return (
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 800 600" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="bg-gradient" cx="30%" cy="40%" r="60%">
          <stop offset="0%" stopColor="#2756A4" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#194D9F" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="800" height="600" fill="url(#bg-gradient)" />
      <circle cx="650" cy="100" r="200" fill="white" fillOpacity="0.03" />
      <circle cx="150" cy="450" r="150" fill="white" fillOpacity="0.03" />
      <circle cx="400" cy="300" r="300" fill="white" fillOpacity="0.02" />
      <line x1="0" y1="200" x2="800" y2="200" stroke="white" strokeOpacity="0.05" strokeWidth="1" />
      <line x1="0" y1="400" x2="800" y2="400" stroke="white" strokeOpacity="0.05" strokeWidth="1" />
      <line x1="200" y1="0" x2="200" y2="600" stroke="white" strokeOpacity="0.05" strokeWidth="1" />
      <line x1="600" y1="0" x2="600" y2="600" stroke="white" strokeOpacity="0.05" strokeWidth="1" />
    </svg>
  );
}

function CarouselContent({ pathname }: { pathname: string }) {
  const { t } = useTranslation();

  const slideIndex = pathname.startsWith('/forgot-password')
    ? 1
    : pathname.startsWith('/subscription')
    ? 2
    : 0;

  const slides = [
    { title: t('carousel.slide1Title'), desc: t('carousel.slide1Desc') },
    { title: t('carousel.slide2Title'), desc: t('carousel.slide2Desc') },
    { title: t('carousel.slide3Title'), desc: t('carousel.slide3Desc') },
  ];

  const slide = slides[slideIndex] || slides[0];

  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 max-w-md">
      <h3 className="text-xl font-semibold mb-3">{slide.title}</h3>
      <p className="text-white/80 text-sm leading-relaxed">{slide.desc}</p>
    </div>
  );
}
